import React, { useState, useMemo, useEffect } from 'react';
import pluralize from 'pluralize';
import { useApp } from '../../contexts/AppContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useAppNavigation } from '../../hooks/useAppNavigation';
import { notificationService } from '../../services/notifications';
import { audioFilesService } from '../../services/audioFiles';
import { Folder, AudioFile } from '../../types';
import { FolderAddIcon, SearchIcon, WarningIcon } from '../../icons';
import SidebarCollapseButton from '../ui/SidebarCollapseButton';
import ConfirmationModal from '../ui/ConfirmationModal';
import FolderTree from './FolderTree';
import FolderModal from './FolderModal';
import FileModal from './FileModal';

interface FilesSidebarProps {
  folders: Folder[];
  files: AudioFile[];
  currentFolderId?: string | null;
  onCreateFolder: (folderData: { name: string }) => Promise<void>;
  onEditFolder: (
    folderId: string,
    folderData: { name: string }
  ) => Promise<void>;
  onDeleteFolder: (folderId: string) => Promise<void>;
  onFolderSelect: (folderId: string | null) => void;
  onFilesUpload: (files: FileList) => void;
  onFileUploaded?: (file: AudioFile) => void;
  onUploadError?: (error: string) => void;
  isUploading?: boolean;
  onCollapse?: () => void;
  setFiles: React.Dispatch<React.SetStateAction<AudioFile[]>>;
  loadFiles: () => Promise<void>;
  onFileDeleted?: (deletedFileId: string) => void;
  onFileMoved?: (
    fileId: string,
    fromFolderId: string | null,
    toFolderId: string | null
  ) => void;
  onFileEdited?: (updatedFile: AudioFile) => void;
}

const FilesSidebar: React.FC<FilesSidebarProps> = ({
  folders,
  files,
  currentFolderId,
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
  onFolderSelect,
  onFilesUpload,
  onFileUploaded,
  onUploadError,
  isUploading = false,
  onCollapse,
  setFiles,
  loadFiles,
  onFileDeleted,
  onFileMoved,
  onFileEdited,
}) => {
  const { state } = useApp();
  const { serviceConfiguration } = usePreferences();
  const { selectAudioFile } = useAppNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState<'create' | 'edit'>(
    'create'
  );
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(['common'])
  );
  const [draggedFile, setDraggedFile] = useState<AudioFile | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    file: AudioFile | null;
  }>({
    isOpen: false,
    file: null,
  });
  const [deleteFolderModal, setDeleteFolderModal] = useState<{
    isOpen: boolean;
    folder: Folder | null;
    fileCount: number;
  }>({
    isOpen: false,
    folder: null,
    fileCount: 0,
  });
  const [showFileModal, setShowFileModal] = useState(false);
  const [editingFile, setEditingFile] = useState<AudioFile | null>(null);

  // Auto-expand folder when file or folder is selected via URL
  useEffect(() => {
    let targetFolderId: string | null = null;

    if (state.selectedAudioFile) {
      // For selected file, expand its containing folder
      targetFolderId = state.selectedAudioFile.folderId || 'common';
    } else if (state.selectedFolder) {
      // For selected folder, expand the folder itself
      targetFolderId =
        state.selectedFolder.id === 'common'
          ? 'common'
          : state.selectedFolder.id;
    }

    // Only expand if we have a target and it isn't already expanded
    if (targetFolderId && !expandedFolders.has(targetFolderId)) {
      setExpandedFolders(new Set([targetFolderId]));
    }
  }, [state.selectedAudioFile, state.selectedFolder, expandedFolders]);

  // Generate accepted file formats from service configuration
  const acceptedFormats = useMemo(() => {
    if (!serviceConfiguration) return [];

    // Convert MIME types to extensions for file picker
    const mimeToExtensionMap: { [key: string]: string } = {
      'audio/mp3': 'MP3',
      'audio/mpeg': 'MP3',
      'video/mp4': 'MP4',
      'audio/mp4': 'MP4',
      'audio/wav': 'WAV',
      'audio/x-wav': 'WAV',
      'audio/m4a': 'M4A',
      'audio/x-m4a': 'M4A',
      'audio/flac': 'FLAC',
      'audio/aac': 'AAC',
      'audio/ogg': 'OGG',
      'audio/oga': 'OGA',
      'audio/opus': 'OPUS',
      'audio/x-ms-wma': 'WMA',
      'audio/aiff': 'AIFF',
      'audio/x-aiff': 'AIFF',
      'video/webm': 'WEBM',
      'audio/webm': 'WEBM',
      'video/quicktime': 'MOV',
      'video/x-msvideo': 'AVI',
      'video/mpeg': 'MPEG',
      'audio/amr': 'AMR',
      'audio/3gpp': '3GP',
      'video/3gpp': '3GP',
      'audio/3gpp2': '3G2',
      'video/3gpp2': '3G2',
    };

    const extensions = serviceConfiguration.file_constraints.supported_formats
      .map((mimeType) => mimeToExtensionMap[mimeType])
      .filter(Boolean);

    // Remove duplicates and return
    return Array.from(new Set(extensions));
  }, [serviceConfiguration]);

  const handleCreateFolder = () => {
    setFolderModalMode('create');
    setEditingFolder(null);
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder: Folder) => {
    setFolderModalMode('edit');
    setEditingFolder(folder);
    setShowFolderModal(true);
  };

  const handleFolderSubmit = async (folderData: { name: string }) => {
    if (folderModalMode === 'create') {
      await onCreateFolder(folderData);
    } else if (editingFolder) {
      await onEditFolder(editingFolder.id, folderData);
    }
  };

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      // If clicking the same folder that's already expanded, just collapse it
      newExpanded.delete(folderId);
    } else {
      // If clicking a different folder, collapse all others and expand this one
      newExpanded.clear();
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const expandOnlyFolder = (folderId: string) => {
    // If clicking the same folder that's already expanded, don't change anything
    // If clicking a different folder, expand only that folder
    if (!expandedFolders.has(folderId)) {
      setExpandedFolders(new Set([folderId]));
    }
  };

  const filteredFolders = (folders || [])
    .filter(
      (folder) =>
        folder &&
        folder.name &&
        folder.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // Common folder always comes first
      if (
        a.name.toLowerCase() === 'common' &&
        b.name.toLowerCase() !== 'common'
      )
        return -1;
      if (
        b.name.toLowerCase() === 'common' &&
        a.name.toLowerCase() !== 'common'
      )
        return 1;
      // Otherwise sort alphabetically by name
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });

  const filteredFiles = (files || [])
    .filter(
      (file) =>
        file &&
        file.name &&
        file.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  // When searching, also include folders that contain matching files
  const foldersWithMatchingFiles = searchQuery.trim()
    ? (folders || []).filter((folder) =>
        filteredFiles.some((file) => file.folderId === folder.id)
      )
    : [];

  const allRelevantFolders = searchQuery.trim()
    ? Array.from(
        new Set([...filteredFolders, ...foldersWithMatchingFiles])
      ).sort((a, b) => {
        // Common folder always comes first
        if (
          a.name.toLowerCase() === 'common' &&
          b.name.toLowerCase() !== 'common'
        )
          return -1;
        if (
          b.name.toLowerCase() === 'common' &&
          a.name.toLowerCase() !== 'common'
        )
          return 1;
        // Otherwise sort alphabetically by name
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      })
    : filteredFolders;

  const getRootFiles = () => filteredFiles.filter((file) => !file.folderId);
  const getFolderFiles = (folderId: string) =>
    filteredFiles.filter((file) => file.folderId === folderId);

  // When searching, show all matching files regardless of folder expansion
  const shouldShowFolderFiles = (folderId: string) => {
    if (searchQuery.trim()) {
      // When searching, show files if there are matching files in this folder
      return getFolderFiles(folderId).length > 0;
    }
    // Normal behavior: only show if folder is expanded
    return expandedFolders.has(folderId);
  };

  const handleFileDragStart = (file: AudioFile) => {
    setDraggedFile(file);
  };

  const handleFileDragEnd = () => {
    setDraggedFile(null);
    setDragOverFolder(null);
  };

  const handleFolderDragOver = (
    e: React.DragEvent,
    folderId: string | null
  ) => {
    e.preventDefault();
    if (draggedFile && draggedFile.folderId !== folderId) {
      setDragOverFolder(folderId);
    }
  };

  const handleFolderDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverFolder(null);
  };

  const handleFolderDrop = async (
    e: React.DragEvent,
    targetFolderId: string | null
  ) => {
    e.preventDefault();
    setDragOverFolder(null);

    if (!draggedFile || draggedFile.folderId === targetFolderId) {
      return;
    }

    try {
      const fromFolderId = draggedFile.folderId || null;

      // Call the move API
      await audioFilesService.moveAudioFile(draggedFile.id, targetFolderId);

      // Notify parent component about the move
      onFileMoved?.(draggedFile.id, fromFolderId, targetFolderId);

      // Update local state to reflect the move
      setFiles((prev) =>
        prev.map((file) =>
          file.id === draggedFile.id
            ? { ...file, folderId: targetFolderId }
            : file
        )
      );

      // Reload files to ensure consistency with server
      loadFiles();

      const targetName =
        targetFolderId === null
          ? 'Common'
          : filteredFolders.find((f) => f.id === targetFolderId)?.name ||
            'Unknown';
      notificationService.success(
        `Moved "${draggedFile.filename}" to "${targetName}"`
      );
    } catch (error) {
      console.error('Failed to move file:', error);
      notificationService.error(
        `Failed to move file: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  };

  const handleDeleteFile = (file: AudioFile) => {
    setDeleteConfirmModal({ isOpen: true, file });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmModal.file) return;

    const deletedFileId = deleteConfirmModal.file.id;

    try {
      await audioFilesService.deleteAudioFile(deletedFileId);

      // Notify parent component about the deletion
      onFileDeleted?.(deletedFileId);

      notificationService.success(
        `Deleted "${deleteConfirmModal.file.filename}"`
      );
    } catch (error) {
      console.error('Failed to delete file:', error);
      notificationService.error(
        `Failed to delete file: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setDeleteConfirmModal({ isOpen: false, file: null });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmModal({ isOpen: false, file: null });
  };

  const handleDeleteFolder = (folder: Folder) => {
    const fileCount = getFolderFiles(folder.id).length;
    setDeleteFolderModal({ isOpen: true, folder, fileCount });
  };

  const handleConfirmDeleteFolder = async () => {
    if (!deleteFolderModal.folder) return;

    try {
      await onDeleteFolder(deleteFolderModal.folder.id);
      notificationService.success(
        `Deleted folder "${deleteFolderModal.folder.name}"`
      );
    } catch (error) {
      console.error('Failed to delete folder:', error);
      notificationService.error(
        `Failed to delete folder: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setDeleteFolderModal({ isOpen: false, folder: null, fileCount: 0 });
    }
  };

  const handleCancelDeleteFolder = () => {
    setDeleteFolderModal({ isOpen: false, folder: null, fileCount: 0 });
  };

  const handleFileSelect = (file: AudioFile) => {
    selectAudioFile(file);
  };

  const handleEditFile = (file: AudioFile, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingFile(file);
    setShowFileModal(true);
  };

  const handleAudioFileSubmit = async (fileData: { name: string }) => {
    if (!editingFile) return;

    const updatedFile = await audioFilesService.updateAudioFile(
      editingFile.id,
      fileData
    );

    // Update local state
    setFiles((prevFiles) =>
      prevFiles.map((f) => (f.id === editingFile.id ? updatedFile : f))
    );

    // Reload files to ensure consistency
    loadFiles();

    // Notify parent component about the file update
    onFileEdited?.(updatedFile);

    setEditingFile(null);
  };

  return (
    <div className="w-full md:w-96 h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Header */}
      <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
            Files
          </h2>
          <div className="flex items-center space-x-1">
            <button
              onClick={handleCreateFolder}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Create folder"
            >
              <FolderAddIcon
                size={20}
                className="text-gray-600 dark:text-gray-300"
              />
            </button>
            {onCollapse && (
              <SidebarCollapseButton
                onClick={onCollapse}
                side="left"
                title="Collapse file menu"
              />
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <SearchIcon
            size={16}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search files and folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* File Tree */}
        <div className="px-3 md:px-4 pt-3 md:pt-4 pb-3 md:pb-4">
          <FolderTree
            folders={folders}
            files={files}
            currentFolderId={currentFolderId}
            expandedFolders={expandedFolders}
            draggedFile={draggedFile}
            dragOverFolder={dragOverFolder}
            searchQuery={searchQuery}
            acceptedFormats={acceptedFormats}
            isUploading={isUploading}
            selectedFolder={state.selectedFolder}
            selectedAudioFile={state.selectedAudioFile}
            getRootFiles={getRootFiles}
            getFolderFiles={getFolderFiles}
            shouldShowFolderFiles={shouldShowFolderFiles}
            allRelevantFolders={allRelevantFolders}
            filteredFiles={filteredFiles}
            onFolderSelect={onFolderSelect}
            toggleFolder={toggleFolder}
            expandOnlyFolder={expandOnlyFolder}
            onFilesUpload={onFilesUpload}
            onFileUploaded={onFileUploaded}
            onUploadError={onUploadError}
            onFolderDragOver={handleFolderDragOver}
            onFolderDragLeave={handleFolderDragLeave}
            onFolderDrop={handleFolderDrop}
            onFileDragStart={handleFileDragStart}
            onFileDragEnd={handleFileDragEnd}
            onFileSelect={handleFileSelect}
            onEditFolder={handleEditFolder}
            onDeleteFolder={handleDeleteFolder}
            onEditFile={handleEditFile}
            onDeleteFile={handleDeleteFile}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="p-2 md:p-4 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 text-right">
        {(files || []).length} files • {(folders || []).length + 1} folders
      </div>

      {/* Folder Modal */}
      <FolderModal
        isOpen={showFolderModal}
        onClose={() => setShowFolderModal(false)}
        onSubmit={handleFolderSubmit}
        mode={folderModalMode}
        initialData={editingFolder ? { name: editingFolder.name } : undefined}
      />

      {/* File Modal */}
      <FileModal
        isOpen={showFileModal}
        onClose={() => setShowFileModal(false)}
        onSubmit={handleAudioFileSubmit}
        initialData={editingFile ? { name: editingFile.name } : undefined}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmModal.isOpen}
        title="Delete Audio File"
        message={`Are you sure you want to delete "${deleteConfirmModal.file?.filename}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        variant="danger"
      />

      {/* Delete Folder Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteFolderModal.isOpen}
        title="Delete Folder"
        message={
          <div>
            <p className="mb-3">
              Are you sure you want to delete "{deleteFolderModal.folder?.name}
              "?
            </p>
            {deleteFolderModal.fileCount > 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md p-3">
                <div className="flex">
                  <WarningIcon
                    size={20}
                    className="text-yellow-400 mr-2 flex-shrink-0"
                  />
                  <div>
                    <p className="text-yellow-800 dark:text-yellow-200 font-medium">
                      Warning!
                    </p>
                    <p className="text-yellow-700 dark:text-yellow-300 text-xs mt-1">
                      This will permanently delete the folder and ALL{' '}
                      {pluralize('file', deleteFolderModal.fileCount, true)}{' '}
                      inside it.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              This action cannot be undone.
            </p>
          </div>
        }
        confirmText="Delete Folder"
        onConfirm={handleConfirmDeleteFolder}
        onCancel={handleCancelDeleteFolder}
        variant="danger"
      />
    </div>
  );
};

export default FilesSidebar;
